<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">
            <div class="header-grid">
              <div class="form-group">
                <label class="form-label">Supplier <span class="text-danger">*</span></label>
                <model-list-select
                  :list="vendorList"
                  v-model="header.vendor"
                  option-value="vendorid"
                  option-text="vendorname"
                  placeholder="Select Supplier"
                  :disabled="Boolean(returnId)"
                  class="mls-field"
                />
              </div>
              <div class="form-group">
                <label class="form-label">Return Date</label>
                <b-form-input v-model="header.returndate" type="date" class="form-ctrl" :disabled="isReadonly" />
              </div>
              <div class="form-group">
                <label class="form-label">Return Bill</label>
                <b-form-input :value="header.billno || 'Will be generated on draft save'" readonly class="form-ctrl bg-light" />
              </div>
              <div class="form-group">
                <label class="form-label">Status</label>
                <div class="status-badge" :class="statusClass">{{ header.status || "NEW" }}</div>
              </div>
            </div>

            <div class="notes-grid">
              <div class="form-group">
                <label class="form-label">Reason</label>
                <b-form-textarea v-model="header.reason" rows="2" class="form-ctrl textarea-ctrl" :disabled="isReadonly" />
              </div>
              <div class="form-group">
                <label class="form-label">Remarks</label>
                <b-form-textarea v-model="header.remarks" rows="2" class="form-ctrl textarea-ctrl" :disabled="isReadonly" />
              </div>
            </div>

            <div v-if="!header.vendor" class="empty-panel">
              Select a supplier to load returnable batches.
            </div>

            <template v-else>
              <div class="section-head">
                <div>
                  <h4>Returnable Batches</h4>
                  <p>Batch-level rows with remaining quantity greater than zero.</p>
                </div>
                <button class="mini-btn" @click="fetchReturnableBatches" :disabled="batchLoading">
                  {{ batchLoading ? "Loading..." : "Refresh Batches" }}
                </button>
              </div>

              <div v-if="batchLoading" class="empty-panel">Loading batches...</div>
              <div v-else-if="!availableBatches.length" class="empty-panel">
                No returnable batches available for this supplier.
              </div>
              <div v-else class="table-responsive mb-4">
                <table class="detail-table">
                  <thead>
                    <tr>
                      <th>Bill No</th>
                      <th>Invoice Date</th>
                      <th>Item</th>
                      <th>Batch</th>
                      <th>Purchased</th>
                      <th>Remaining</th>
                      <th>Price</th>
                      <th>Tax Type</th>
                      <th>Tax %</th>
                      <th>Taxable</th>
                      <th>CGST</th>
                      <th>SGST</th>
                      <th>IGST</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="batch in availableBatches" :key="batch.purchaseitemid">
                      <td>{{ batch.billno || batch.purchaseid }}</td>
                      <td>{{ formatDate(batch.invoicedate) }}</td>
                      <td>{{ batch.itemname }}</td>
                      <td>{{ batch.batchno || "-" }}</td>
                      <td>{{ formatQty(batch.quantity, batch.freequantity) }}</td>
                      <td>{{ formatNumber(batch.remainingqty) }}</td>
                      <td>Rs. {{ formatNumber(batch.purchaseprice) }}</td>
                      <td>{{ batch.taxtype || "GST" }}<span v-if="batch.istaxinclusive" class="text-muted"> (Incl)</span></td>
                      <td>{{ formatNumber(batch.taxpercent) }}</td>
                      <td>Rs. {{ formatNumber(batch.taxablevalue) }}</td>
                      <td>Rs. {{ formatNumber(batch.cgst) }}</td>
                      <td>Rs. {{ formatNumber(batch.sgst) }}</td>
                      <td>Rs. {{ formatNumber(batch.igst) }}</td>
                      <td class="text-end">
                        <button class="line-btn" @click="addBatch(batch)" :disabled="isReadonly || hasSelectedBatch(batch.purchaseitemid)">
                          {{ hasSelectedBatch(batch.purchaseitemid) ? "Added" : "Add" }}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="section-head">
                <div>
                  <h4>Selected Return Items</h4>
                  <p>Choose exact batch rows and quantities to return.</p>
                </div>
              </div>

              <div v-if="!items.length" class="empty-panel">
                Add at least one batch to create a purchase return draft.
              </div>
              <div v-else class="table-responsive">
                <table class="detail-table">
                  <thead>
                    <tr>
                      <th>Bill</th>
                      <th>Item</th>
                      <th>Batch</th>
                      <th>Max Return</th>
                      <th>Return Qty</th>
                      <th>Price</th>
                      <th>Tax Type</th>
                      <th>Tax %</th>
                      <th>Taxable</th>
                      <th>CGST</th>
                      <th>SGST</th>
                      <th>IGST</th>
                      <th>Tax</th>
                      <th>Amount</th>
                      <th>Reason</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="line in items" :key="line.purchaseitemid">
                      <td>{{ line.purchasebillno || line.purchaseid }}</td>
                      <td>{{ line.itemname }}</td>
                      <td>{{ line.batchno || "-" }}</td>
                      <td>{{ formatNumber(line.maxreturnqty) }}</td>
                      <td>
                        <b-form-input
                          v-model.number="line.returnqty"
                          type="number"
                          min="0"
                          :max="line.maxreturnqty"
                          class="table-input"
                          :disabled="isReadonly"
                          @input="normalizeLine(line)"
                        />
                      </td>
                      <td>Rs. {{ formatNumber(line.purchaseprice) }}</td>
                      <td>{{ line.taxtype || "GST" }}<span v-if="line.istaxinclusive" class="text-muted"> (Incl)</span></td>
                      <td>{{ formatNumber(line.taxpercent) }}</td>
                      <td>Rs. {{ formatNumber(line.taxablevalue) }}</td>
                      <td>Rs. {{ formatNumber(line.cgst) }}</td>
                      <td>Rs. {{ formatNumber(line.sgst) }}</td>
                      <td>Rs. {{ formatNumber(line.igst) }}</td>
                      <td>Rs. {{ formatNumber(line.taxamount) }}</td>
                      <td>Rs. {{ formatNumber(line.netamount) }}</td>
                      <td>
                        <b-form-input v-model="line.reason" class="table-input" :disabled="isReadonly" />
                      </td>
                      <td class="text-end">
                        <button class="remove-btn" @click="removeLine(line.purchaseitemid)" :disabled="isReadonly">Remove</button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="summary-card">
                <div class="summary-row">
                  <span>Total Amount</span>
                  <strong>Rs. {{ formatNumber(summary.totalamount) }}</strong>
                </div>
                <div class="summary-row">
                  <span>Total Tax</span>
                  <strong>Rs. {{ formatNumber(summary.totaltax) }}</strong>
                </div>
                <div class="summary-row summary-row-net">
                  <span>Net Amount</span>
                  <strong>Rs. {{ formatNumber(summary.netamount) }}</strong>
                </div>
              </div>
            </template>

            <div class="footer-actions">
              <button class="btn btn-primary" @click="saveDraft" :disabled="saving || isReadonly">
                {{ saving ? "Saving..." : returnId ? "Update Draft" : "Save Draft" }}
              </button>
              <button
                v-if="returnId && header.status === 'DRAFT'"
                class="btn btn-success"
                @click="confirmReturn"
                :disabled="saving"
              >
                Confirm Return
              </button>
              <button
                v-if="returnId && ['DRAFT', 'CONFIRMED'].includes(header.status)"
                class="btn btn-danger"
                @click="cancelReturn"
                :disabled="saving"
              >
                Cancel Return
              </button>
              <router-link to="/purchasereturnlist" class="btn btn-secondary">Back</router-link>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ModelListSelect } from "vue-search-select";
import axios from "axios";
import Swal from "sweetalert2";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

export default {
  name: "createpurchasereturn",
  components: { ModelListSelect },
  data() {
    return {
      loading: false,
      saving: false,
      batchLoading: false,
      vendorList: [],
      availableBatches: [],
      items: [],
      header: {
        vendor: null,
        vendorname: "",
        billno: "",
        returndate: this.getToday(),
        locationid: this.$store.getters.getCurrentLocation?.locationid || "",
        status: "NEW",
        reason: "",
        remarks: "",
      },
    };
  },
  computed: {
    returnId() {
      return this.$route.query.id || "";
    },
    title() {
      return this.returnId ? "Edit Purchase Return" : "Create Purchase Return";
    },
    title1() {
      return this.returnId ? "Review, update, confirm, or cancel draft returns" : "Create batch-level purchase return drafts";
    },
    isReadonly() {
      return Boolean(this.returnId) && this.header.status !== "DRAFT";
    },
    statusClass() {
      return {
        "badge-draft": this.header.status === "DRAFT",
        "badge-confirmed": this.header.status === "CONFIRMED",
        "badge-cancelled": this.header.status === "CANCELLED",
      };
    },
    summary() {
      const totalamount = this.items.reduce((sum, line) => sum + Number(line.amount || 0), 0);
      const totaltax = this.items.reduce((sum, line) => sum + Number(line.taxamount || 0), 0);
      const netamount = this.items.reduce((sum, line) => sum + Number(this.lineTotal(line) || 0), 0);
      return {
        totalamount: Number(totalamount.toFixed(2)),
        totaltax: Number(totaltax.toFixed(2)),
        netamount: Number(netamount.toFixed(2)),
      };
    },
    locationOptions() {
      return (this.$store.getters.getLocations || []).map((location) => ({
        value: location.locationid,
        text: location.locationname,
      }));
    },
  },
  watch: {
    "header.vendor": {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue) return;
        if (!this.returnId) this.items = [];
        await this.fetchReturnableBatches();
      },
    },
    "header.locationid": {
      async handler(newValue, oldValue) {
        if (!newValue || newValue === oldValue || !this.header.vendor) return;
        if (!this.returnId) this.items = [];
        await this.fetchReturnableBatches();
      },
    },
  },
  async beforeMount() {
    await this.fetchVendorList();
    if (this.returnId) {
      await this.loadReturn();
    }
  },
  methods: {
    getToday() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    },
    getCurrentUserId() {
      const attrs = this.$store?.getters?.getUserDetails;
      if (!Array.isArray(attrs)) return null;
      const sub = attrs.find((attr) => attr.Name === "sub");
      return sub?.Value || null;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatQty(quantity, freequantity) {
      const qty = Number(quantity || 0);
      const free = Number(freequantity || 0);
      return free ? `${qty} + ${free}` : `${qty}`;
    },
    lineTotal(line) {
      const amount = Number(line.amount || 0);
      const tax = Number(line.taxamount || 0);
      return line.istaxinclusive ? amount : (amount + tax);
    },
    async fetchVendorList() {
      try {
        const response = await axios.get("/v1/vendors");
        this.vendorList = response.data || [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load suppliers." });
      }
    },
    async fetchReturnableBatches() {
      if (!this.header.vendor) {
        this.availableBatches = [];
        return;
      }
      this.batchLoading = true;
      try {
        const response = await axios.get("/v1/purchase-returns/returnable-batches", {
          params: {
            vendor_id: this.header.vendor,
            locationid: this.header.locationid || this.$store.getters.getCurrentLocation?.locationid || undefined,
            current_returnid: this.returnId || undefined,
          },
        });
        this.availableBatches = response.data || [];
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load returnable batches." });
      } finally {
        this.batchLoading = false;
      }
    },
    mapLineFromBatch(batch, overrides = {}) {
      const returnqty = Number(overrides.returnqty ?? 1);
      const purchaseprice = Number(batch.purchaseprice || 0);
      const taxpercent = Number(batch.taxpercent || 0);
      const istaxinclusive = Boolean(batch.istaxinclusive);
      const gross = Number((returnqty * purchaseprice).toFixed(2));
      const taxablevalue = istaxinclusive && taxpercent > 0
        ? Number((gross * 100 / (100 + taxpercent)).toFixed(2))
        : gross;
      const taxamount = istaxinclusive
        ? Number((gross - taxablevalue).toFixed(2))
        : Number((taxablevalue * taxpercent / 100).toFixed(2));
      const cgst = Number(overrides.cgst ?? (taxamount / 2)).toFixed(2);
      const sgst = Number(overrides.sgst ?? (taxamount / 2)).toFixed(2);
      const igst = Number(0).toFixed(2);
      const amount = istaxinclusive ? gross : taxablevalue;
      return {
        purchaseitemid: batch.purchaseitemid,
        purchaseid: batch.purchaseid,
        purchasebillno: batch.purchasebillno || batch.billno,
        itemid: batch.itemid,
        itemname: batch.itemname,
        batchno: batch.batchno,
        expirydate: batch.expirydate,
        purchaseprice,
        taxtype: batch.taxtype || "GST",
        taxpercent,
        taxablevalue,
        cgst: Number(cgst),
        sgst: Number(sgst),
        igst: Number(igst),
        istaxinclusive,
        maxreturnqty: Number(batch.remainingqty || 0),
        returnqty,
        amount,
        taxamount,
        netamount: Number(this.lineTotal({ amount, taxamount, istaxinclusive }).toFixed(2)),
        reason: overrides.reason || "",
      };
    },
    hasSelectedBatch(purchaseitemid) {
      return this.items.some((line) => line.purchaseitemid === purchaseitemid);
    },
    addBatch(batch) {
      if (this.isReadonly) return;
      if (this.hasSelectedBatch(batch.purchaseitemid)) return;
      this.items.push(this.mapLineFromBatch(batch));
    },
    removeLine(purchaseitemid) {
      this.items = this.items.filter((line) => line.purchaseitemid !== purchaseitemid);
    },
    normalizeLine(line) {
      const max = Number(line.maxreturnqty || 0);
      let qty = Number(line.returnqty || 0);
      if (qty < 0) qty = 0;
      if (qty > max) qty = max;
      line.returnqty = qty;
      const gross = Number((qty * Number(line.purchaseprice || 0)).toFixed(2));
      if (line.istaxinclusive && Number(line.taxpercent || 0) > 0) {
        line.taxablevalue = Number((gross * 100 / (100 + Number(line.taxpercent || 0))).toFixed(2));
        line.amount = gross;
        line.taxamount = Number((gross - line.taxablevalue).toFixed(2));
      } else {
        line.taxablevalue = gross;
        line.amount = gross;
        line.taxamount = Number((line.taxablevalue * Number(line.taxpercent || 0) / 100).toFixed(2));
      }
      line.cgst = Number((line.taxamount / 2).toFixed(2));
      line.sgst = Number((line.taxamount / 2).toFixed(2));
      line.igst = 0;
      line.netamount = Number(this.lineTotal(line).toFixed(2));
    },
    buildPayload() {
      return {
        vendor: this.header.vendor,
        locationid: this.header.locationid || null,
        returndate: this.header.returndate,
        reason: this.header.reason || null,
        remarks: this.header.remarks || null,
        createdby: this.getCurrentUserId(),
        items: this.items.map((line) => ({
          purchaseitemid: line.purchaseitemid,
          returnqty: Number(line.returnqty || 0),
          reason: line.reason || null,
        })),
      };
    },
    validateDraft() {
      if (!this.header.vendor) {
        Swal.fire("Supplier is required");
        return false;
      }
      if (!this.items.length) {
        Swal.fire("Add at least one batch to return");
        return false;
      }
      for (let i = 0; i < this.items.length; i += 1) {
        const line = this.items[i];
        if (!(Number(line.returnqty) > 0)) {
          Swal.fire(`Return quantity must be greater than 0 for row ${i + 1}`);
          return false;
        }
        if (Number(line.returnqty) > Number(line.maxreturnqty)) {
          Swal.fire(`Return quantity cannot exceed remaining quantity for row ${i + 1}`);
          return false;
        }
      }
      return true;
    },
    async saveDraft() {
      if (!this.validateDraft()) return;
      this.saving = true;
      try {
        const payload = this.buildPayload();
        if (this.returnId) {
          await axios.put(`/v1/purchase-returns/${this.returnId}`, payload);
          Swal.fire({ icon: "success", title: "Draft updated", timer: 1400, showConfirmButton: false });
        } else {
          const response = await axios.post("/v1/purchase-returns", payload);
          const newId = response.data?.[0];
          Swal.fire({ icon: "success", title: "Draft saved", timer: 1400, showConfirmButton: false });
          if (newId) {
            await this.$router.replace({ path: "/createpurchasereturn", query: { id: newId } });
            await this.loadReturn();
          }
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to save purchase return draft." });
      } finally {
        this.saving = false;
      }
    },
    async loadReturn() {
      if (!this.returnId) return;
      this.loading = true;
      try {
        const response = await axios.get(`/v1/purchase-returns/${this.returnId}`);
        const detail = response.data;
        this.header = {
          vendor: detail.vendor,
          vendorname: detail.vendorname || "",
          billno: detail.billno,
          returndate: detail.returndate || this.getToday(),
          locationid: detail.locationid || this.$store.getters.getCurrentLocation?.locationid || "",
          status: detail.status,
          reason: detail.reason || "",
          remarks: detail.remarks || "",
        };
        await this.fetchReturnableBatches();
        this.items = (detail.items || []).map((item) => {
          const batch = this.availableBatches.find((row) => row.purchaseitemid === item.purchaseitemid) || {
            purchaseitemid: item.purchaseitemid,
            purchaseid: item.purchaseid,
            billno: item.purchasebillno,
            itemid: item.itemid,
            itemname: item.itemname,
            batchno: item.batchno,
            expirydate: item.expirydate,
            purchaseprice: item.purchaseprice,
            taxtype: item.taxtype,
            taxpercent: item.taxpercent,
            taxablevalue: item.taxablevalue,
            cgst: item.cgst,
            sgst: item.sgst,
            igst: item.igst,
            istaxinclusive: item.istaxinclusive,
            remainingqty: item.returnqty,
          };
          return this.mapLineFromBatch(batch, {
            returnqty: item.returnqty,
            cgst: item.cgst,
            sgst: item.sgst,
            igst: item.igst,
            reason: item.reason,
          });
        });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to load purchase return." });
      } finally {
        this.loading = false;
      }
    },
    async confirmReturn() {
      const result = await Swal.fire({
        title: "Confirm purchase return?",
        text: "This will reduce stock and batch remaining quantity.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, confirm",
      });
      if (!result.isConfirmed) return;

      this.saving = true;
      try {
        await axios.post(`/v1/purchase-returns/${this.returnId}/confirm`, {
          actorid: this.getCurrentUserId(),
        });
        Swal.fire({ icon: "success", title: "Return confirmed", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to confirm purchase return." });
      } finally {
        this.saving = false;
      }
    },
    async cancelReturn() {
      const result = await Swal.fire({
        title: "Cancel purchase return?",
        text: this.header.status === "CONFIRMED"
          ? "This will reverse the stock movement with a new ledger entry."
          : "This draft return will be cancelled.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;

      this.saving = true;
      try {
        await axios.post(`/v1/purchase-returns/${this.returnId}/cancel`, {
          actorid: this.getCurrentUserId(),
        });
        Swal.fire({ icon: "success", title: "Return cancelled", timer: 1400, showConfirmButton: false });
        await this.loadReturn();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to cancel purchase return." });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.header-grid,
.notes-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;
}
.header-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
.notes-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.form-label {
  display: block;
  margin-bottom: 6px;
  color: #6b7280;
  font-size: 0.82rem;
  font-weight: 700;
}
.form-ctrl {
  min-height: 38px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.textarea-ctrl {
  min-height: 88px;
}
.status-badge {
  min-height: 38px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 8px;
  font-weight: 700;
  border: 1px solid #d1d5db;
  background: #f9fafb;
  color: #374151;
}
.badge-draft {
  background: #fff7ed;
  border-color: #fdba74;
  color: #c2410c;
}
.badge-confirmed {
  background: #f0fdf4;
  border-color: #86efac;
  color: #15803d;
}
.badge-cancelled {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #dc2626;
}
.section-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin: 18px 0 10px;
}
.section-head h4 {
  margin: 0;
  font-size: 1rem;
  color: #111827;
}
.section-head p {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 0.8rem;
}
.mini-btn,
.line-btn,
.remove-btn {
  border: none;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.8rem;
  font-weight: 700;
}
.mini-btn,
.line-btn {
  background: #fff7ed;
  color: #c2410c;
}
.remove-btn {
  background: #fef2f2;
  color: #dc2626;
}
.empty-panel {
  padding: 16px;
  border-radius: 12px;
  border: 1px dashed #d1d5db;
  background: #f9fafb;
  color: #6b7280;
  margin-bottom: 16px;
}
.detail-table {
  width: 100%;
  border-collapse: collapse;
}
.detail-table th,
.detail-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.84rem;
  vertical-align: middle;
}
.detail-table th {
  background: #f8fafc;
  color: #64748b;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.table-input {
  min-width: 110px;
  height: 34px;
}
.summary-card {
  margin-top: 18px;
  margin-left: auto;
  width: min(100%, 320px);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}
.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #f3f4f6;
}
.summary-row-net {
  background: #fff7ed;
  color: #c2410c;
}
.footer-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 20px;
}
@media (max-width: 1100px) {
  .header-grid,
  .notes-grid {
    grid-template-columns: 1fr 1fr;
  }
}
@media (max-width: 768px) {
  .header-grid,
  .notes-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.mls-field .ui.selection.dropdown {
  min-height: 38px !important;
  border-radius: 8px !important;
  border: 1px solid #e5e7eb !important;
}
</style>
